// Tracks build and runtime (server) versions across page loads.
// - Enriches version history (written by reload-loop-breaker before hydration)
//   with runtime server version from /api/version/
// - Attaches version history as Sentry context on all error reports
// - Pushes GTM version-mismatch event when build !== runtime
//
// Replaces the former version-upgrade.client.ts plugin.

import { setContext, setTag } from '@sentry/nuxt'

interface VersionEntry {
  build: string
  runtime: string | null
  t: string
}

// Must match VH_KEY in reload-loop-breaker.ts inline script
const HISTORY_KEY = '__alfred_version_history'

export default defineNuxtPlugin((nuxtApp) => {
  const build = useRuntimeConfig().public.alfredVersion || 'unknown'

  console.warn(`Alfred version: ${build}`)

  onNuxtReady(() => {
    void enrichAndSetContext(build, nuxtApp)
  })
})

async function enrichAndSetContext(build: string, nuxtApp: any) {
  const runtime = await fetchRuntimeVersion()

  if (runtime) {
    console.warn(`Alfred server version: ${runtime}`)
  }

  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- our own localStorage JSON
    const history: VersionEntry[] = raw ? JSON.parse(raw) : []

    // Update the latest entry with runtime version (inline script only sets build)
    const last = history.length > 0 ? history[history.length - 1] : null
    if (last && last.build === build && last.runtime === null && runtime) {
      last.runtime = runtime
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history))
    }

    const prev = history.length > 1 ? history[history.length - 2] : null

    setContext('versionHistory', {
      current: { build, runtime },
      entries: history.map(formatEntry),
    })

    if (prev && prev.build !== build) {
      setTag('version-upgraded', 'true')
      setTag('previous-version', prev.build)
    }

    if (runtime && build !== runtime) {
      setTag('version-mismatch', 'true')

      console.warn('Alfred version mismatch.', build, runtime)

      pushGtmMismatchEvent(nuxtApp, build, runtime)
    }
  } catch {
    // localStorage unavailable
  }
}

async function fetchRuntimeVersion(): Promise<string | null> {
  try {
    const { version } = await $fetch<{ version: string }>('/api/version/', {
      timeout: 10_000,
    })
    return version
  } catch {
    return null
  }
}

function pushGtmMismatchEvent(nuxtApp: any, build: string, runtime: string) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- GTM is untyped
  const gtm = nuxtApp.$gtm
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- GTM is untyped
  if (gtm && typeof gtm.push === 'function') {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- GTM is untyped
    gtm.push({
      event: 'version-mismatch',
      version: runtime,
      alfredVersion: build,
    })
  }
}

function formatEntry(e: VersionEntry): string {
  if (e.runtime && e.runtime !== e.build) {
    return `build:${e.build} runtime:${e.runtime} @ ${e.t}`
  }
  return `${e.build} @ ${e.t}`
}
